/**
 * EventFlowBuilder — builder for Event Flow section.
 *
 * Thin wrapper around shared flow components, configured for event_flow source.
 *
 * Progressive depth loading:
 *   1. Initial fetch: depthTo = CHUNK_SIZE (first 10 levels)
 *   2. Background pre-fetch: next CHUNK_SIZE levels, repeated until all loaded
 *   3. Records are accumulated — FlowGraphBuilder is rebuilt from the full set
 *   4. The user never sees a loading state when expanding the tree
 *
 * Cross-session continuation:
 *   - Clicking a "(none)" node triggers a continuation query
 *   - The query finds users with matching session paths and returns their next session
 *   - Continuation records are merged into the graph under a session-specific namespace
 *   - Nodes from continuation sessions have distinct tinted styling
 */
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { BuilderShell } from '@/components/builder/BuilderShell';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { useSavedReport } from '@/hooks/useSavedReport';
import { useSectionCache } from '@/hooks/useSectionCache';
import { useFlowState } from '@/sections/flow/useFlowState';
import { FlowConfigPanel } from '@/sections/flow/FlowConfigPanel';
import { FlowResultsPanel } from '@/sections/flow/FlowResultsPanel';
import { FlowGraphBuilder, FlowTreeManager } from '@/sections/flow/FlowGraphEngine';
import type { FlowState, FlowSectionConfig, FlowRecord, FlowLevel, FlowDataResponse } from '@/sections/flow/flow.types';

/* ─── Constants ───────────────────────────────────────────────────────────── */

const CHUNK_SIZE = 10;

/* ─── Section Config ──────────────────────────────────────────────────────── */

const SECTION_CONFIG: FlowSectionConfig = {
  source: 'event_flow',
  sectionId: 'event_flow',
  sectionRoute: '/event-flow',
  accentColor: '#06B6D4',
  title: 'Event Flow',
  subtitle: 'Visualize user event sequences as interactive flow trees',
  itemLabel: 'Event',
  itemsLabel: 'Events',
};

/* ─── Cached data shape ───────────────────────────────────────────────────── */

interface CachedFlowData {
  records: FlowRecord[];
  levels: FlowLevel[];
  loadedDepth: number;
  maxAvailableDepth: number;
}

/* ─── Component ───────────────────────────────────────────────────────────── */

export function EventFlowBuilder() {
  const { reportId } = useParams();
  const { state, update, reset, canRun, toFlowPayload, toContinuationPayload, dates, searchParams } = useFlowState('event_flow');

  // Tree levels state (managed locally, updated on node clicks)
  const [levels, setLevels] = useState<FlowLevel[]>([]);
  const [records, setRecords] = useState<FlowRecord[] | null>(null);

  // Depth tracking for progressive loading
  const [loadedDepth, setLoadedDepth] = useState(0);
  const [maxAvailableDepth, setMaxAvailableDepth] = useState(0);
  const prefetchAbortRef = useRef<AbortController | null>(null);
  const prefetchingRef = useRef(false);

  // Cross-session continuation state
  const [continuationLoadingLevels, setContinuationLoadingLevels] = useState<Set<number>>(new Set());
  const [continuationRecordsMap, setContinuationRecordsMap] = useState<Map<number, FlowRecord[]>>(new Map());
  const graphBuilderRef = useRef<FlowGraphBuilder | null>(null);

  const levelsRef = useRef<FlowLevel[]>([]);
  levelsRef.current = levels;

  /* ── Load saved report ────────────────────────────────────────────────── */
  const { savedReport } = useSavedReport<FlowState>({
    reportId,
    sectionId: SECTION_CONFIG.sectionId,
    searchParams,
    update,
    mapConfig: useCallback((config: Record<string, unknown>) => {
      return {
        preset: (config.preset as string) || (config.datePreset as string) || '',
        start: (config.start as string) || (config.startDate as string) || '',
        end: (config.end as string) || (config.endDate as string) || '',
        filterMode: ((config.filterMode as string) || 'exclude') as 'include' | 'exclude',
        events: Array.isArray(config.events)
          ? config.events as string[]
          : typeof config.events === 'string' && config.events
            ? (config.events as string).split(',').filter(Boolean)
            : Array.isArray(config.excludedEvents)
              ? config.excludedEvents as string[]
              : [],
        startFrom: (config.startFrom as string) || (config.startFromEvent as string) || '',
        collapse: Boolean(config.collapse ?? config.collapseRepeats),
        kpi: (config.kpi as string) || (config.kpiEvent as string) || '',
        filters: typeof config.filters === 'string'
          ? config.filters
          : '',
      };
    }, []),
  });

  /* ── Section cache ────────────────────────────────────────────────────── */
  const [cachedData, setCachedData] = useSectionCache<CachedFlowData>('event-flow-builder');

  // Restore from cache on mount
  useMemo(() => {
    if (cachedData && !records) {
      setRecords(cachedData.records);
      setLevels(cachedData.levels);
      setLoadedDepth(cachedData.loadedDepth);
      setMaxAvailableDepth(cachedData.maxAvailableDepth);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Background pre-fetch ─────────────────────────────────────────────── */

  const prefetchNext = useCallback(async (
    currentDepth: number,
    maxDepth: number,
    payloadBuilder: typeof toFlowPayload,
    initialRecords: FlowRecord[],
  ) => {
    if (prefetchingRef.current) return;
    if (currentDepth >= maxDepth) return;

    prefetchingRef.current = true;

    let depth = currentDepth;
    let accumulatedRecords = [...initialRecords];
    while (depth < maxDepth) {
      const depthFrom = depth;
      const depthTo = Math.min(depth + CHUNK_SIZE, maxDepth);

      try {
        const payload = payloadBuilder(depthFrom, depthTo);
        const { data: chunk, meta } = await api.postWithMeta<FlowDataResponse>(
          endpoints.flow.data(),
          payload,
        );

        const newRecords = chunk.flow;
        if (newRecords.length === 0) break;

        accumulatedRecords = [...accumulatedRecords, ...newRecords];
        setRecords([...accumulatedRecords]);
        depth = depthTo;
        setLoadedDepth(depthTo);

        const serverMax = (meta?.maxAvailableDepth as number) ?? maxDepth;
        setMaxAvailableDepth(serverMax);

        const hasMore = (meta?.hasMore as boolean) ?? depthTo < maxDepth;
        if (!hasMore) break;
      } catch {
        break;
      }
    }

    prefetchingRef.current = false;
  }, []);

  // Abort background fetches on unmount
  useEffect(() => {
    return () => {
      prefetchAbortRef.current?.abort();
    };
  }, []);

  /* ── Run query (initial fetch) ──────────────────────────────────────── */
  const queryMutation = useMutation({
    mutationFn: async () => {
      prefetchAbortRef.current?.abort();
      prefetchingRef.current = false;

      const payload = toFlowPayload(undefined, CHUNK_SIZE);
      return api.postWithMeta<FlowDataResponse>(endpoints.flow.data(), payload);
    },
    onSuccess: ({ data: response, meta }) => {
      const flowRecords = response.flow;
      setRecords(flowRecords);

      const builder = new FlowGraphBuilder(flowRecords);
      graphBuilderRef.current = builder;
      const manager = new FlowTreeManager(builder);
      manager.initialize();
      setLevels(manager.levels);

      const initialDepth = CHUNK_SIZE;
      const serverMax = (meta?.maxAvailableDepth as number) ?? CHUNK_SIZE;
      setLoadedDepth(initialDepth);
      setMaxAvailableDepth(serverMax);

      setCachedData({
        records: flowRecords,
        levels: manager.levels,
        loadedDepth: initialDepth,
        maxAvailableDepth: serverMax,
      });

      const hasMore = (meta?.hasMore as boolean) ?? false;
      if (hasMore) {
        prefetchNext(initialDepth, serverMax, toFlowPayload, flowRecords);
      }
    },
  });

  const handleRun = useCallback(() => {
    if (canRun) queryMutation.mutate();
  }, [canRun, queryMutation]);

  const handleLevelsChange = useCallback(
    (newLevels: FlowLevel[]) => {
      setLevels(newLevels);
      if (records) {
        setCachedData({
          records,
          levels: newLevels,
          loadedDepth,
          maxAvailableDepth,
        });
      }
    },
    [records, setCachedData, loadedDepth, maxAvailableDepth],
  );

  /* ── Continuation: build sessionPaths from current levels ───────────── */

  const buildSessionPaths = useCallback((currentLevels: FlowLevel[], clickedLevel: number): string[][] => {
    const sessionPaths: string[][] = [];
    let currentPath: string[] = [];
    let currentSession = 0;

    // Iterate up to but NOT including clickedLevel — the (none) node at
    // clickedLevel represents users whose session ended at the parent event.
    // Including levels[clickedLevel].selected would incorrectly add a
    // previously-selected sibling event to the path, making different (none)
    // nodes at the same level produce identical session paths.
    for (let i = 0; i < clickedLevel; i++) {
      const lev = currentLevels[i];
      const levSession = lev.sessionIndex ?? 0;

      if (levSession !== currentSession && currentPath.length > 0) {
        sessionPaths.push([...currentPath]);
        currentPath = [];
        currentSession = levSession;
      }

      if (lev.selected && lev.selected !== '(none)') {
        currentPath.push(lev.selected);
      }
    }

    if (currentPath.length > 0) {
      sessionPaths.push(currentPath);
    }

    return sessionPaths;
  }, []);

  /* ── Continuation request handler ───────────────────────────────────── */

  const handleContinuationRequest = useCallback(
    async (level: number, currentSessionIndex: number) => {
      if (!records) return;

      const nextSessionIndex = currentSessionIndex + 1;

      // Show loading state
      setContinuationLoadingLevels(prev => new Set(prev).add(level));

      try {
        // Build session paths from the current tree state
        const sessionPaths = buildSessionPaths(levels, level);

        if (sessionPaths.length === 0) return;

        // Fire continuation query
        const payload = toContinuationPayload(sessionPaths, CHUNK_SIZE);
        const { data: response } = await api.postWithMeta<FlowDataResponse>(
          endpoints.flow.continuation(),
          payload,
        );

        const continuationRecords = response.flow;
        if (continuationRecords.length === 0) {
          // No continuation data — just select the (none) node
          const treeManager = new FlowTreeManager(new FlowGraphBuilder(records));
          treeManager.levels = structuredClone(levels);
          treeManager.selectTerminal(level, '(none)');
          setLevels(treeManager.levels);
          return;
        }

        // Backend already filters to continuationStartFrom='app_launch',
        // but guard against edge cases where other FIRST_DIST events slip through.
        const filteredRecords = continuationRecords.filter(r => {
          if (r.kind === 'FIRST_DIST') return r.a === 'app_launch';
          return true;
        });

        // Store continuation records so FlowVisualization can build a complete graph
        const newMap = new Map(continuationRecordsMap);
        newMap.set(nextSessionIndex, filteredRecords);
        setContinuationRecordsMap(newMap);

        // Build a graph with all continuation data merged for tree expansion
        const builder = new FlowGraphBuilder(records);
        graphBuilderRef.current = builder;
        for (const [si, cr] of newMap) {
          builder.mergeContinuation(cr, si);
        }

        // Expand the tree with continuation first nodes
        const treeManager = new FlowTreeManager(builder);
        treeManager.levels = structuredClone(levels);
        treeManager.expandContinuation(level, nextSessionIndex);

        setLevels(treeManager.levels);

        if (records) {
          setCachedData({
            records,
            levels: treeManager.levels,
            loadedDepth,
            maxAvailableDepth,
          });
        }
      } catch (err) {
        console.error('[Flow] Continuation request failed:', err);
        // On error, just select (none) as terminal
        const treeManager = new FlowTreeManager(new FlowGraphBuilder(records));
        treeManager.levels = structuredClone(levels);
        treeManager.selectTerminal(level, '(none)');
        setLevels(treeManager.levels);
      } finally {
        setContinuationLoadingLevels(prev => {
          const next = new Set(prev);
          next.delete(level);
          return next;
        });
      }
    },
    [records, levels, buildSessionPaths, toContinuationPayload, setCachedData, loadedDepth, maxAvailableDepth, continuationRecordsMap],
  );

  const hasRun = queryMutation.isSuccess || queryMutation.isError || !!records;

  return (
    <BuilderShell
      title="New Report"
      subtitle={SECTION_CONFIG.title}
      sectionId={SECTION_CONFIG.sectionId}
      sectionRoute={SECTION_CONFIG.sectionRoute}
      reportId={reportId}
      savedReport={savedReport as { report?: { name?: string; description?: string } } | undefined}
      onReset={() => {
        reset();
        prefetchAbortRef.current?.abort();
        prefetchingRef.current = false;
        setRecords(null);
        setLevels([]);
        setLoadedDepth(0);
        setMaxAvailableDepth(0);
        setContinuationLoadingLevels(new Set());
        setContinuationRecordsMap(new Map());
        graphBuilderRef.current = null;
      }}
      onRun={handleRun}
      isRunning={queryMutation.isPending}
      canRun={canRun}
      configForSave={state as unknown as Record<string, unknown>}
      configPanel={
        <FlowConfigPanel
          state={state}
          onChange={update}
          dates={dates}
          config={SECTION_CONFIG}
        />
      }
    >
      <FlowResultsPanel
        isLoading={queryMutation.isPending}
        isError={queryMutation.isError}
        error={queryMutation.error}
        records={records}
        hasRun={hasRun}
        canRun={canRun}
        onRun={handleRun}
        levels={levels}
        onLevelsChange={handleLevelsChange}
        kpiEvent={state.kpi || undefined}
        accentColor={SECTION_CONFIG.accentColor}
        onContinuationRequest={handleContinuationRequest}
        continuationLoadingLevels={continuationLoadingLevels}
        continuationRecordsMap={continuationRecordsMap}
      />
    </BuilderShell>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

function decodeFiltersForPayload(encoded: string): unknown[] {
  try {
    const parsed = JSON.parse(atob(encoded));
    if (parsed && Array.isArray(parsed.groups)) {
      const groups = parsed.groups as Array<{
        conditions: Array<{ field: string; operator: string; value: string }>;
        conditionOperator?: 'AND' | 'OR';
      }>;
      const validGroups = groups.filter((g) =>
        g.conditions.some((c) => c.field && c.value),
      );
      if (validGroups.length === 0) return [];
      if (validGroups.length === 1) {
        const condOp = validGroups[0].conditionOperator ?? 'AND';
        return validGroups[0].conditions
          .filter((c) => c.field && c.value)
          .map((c, i) => ({
            type: 'filter' as const,
            field: c.field,
            value: c.value,
            comparison: c.operator || '=',
            operator: i === 0 ? null : condOp,
          }));
      }
      const topOp = parsed.groupOperator ?? 'OR';
      return validGroups.map((group, gIdx) => ({
        type: 'group' as const,
        groupOperator: group.conditionOperator ?? 'AND',
        operator: gIdx === 0 ? null : topOp,
        children: group.conditions
          .filter((c) => c.field && c.value)
          .map((c, cIdx) => ({
            type: 'filter' as const,
            field: c.field,
            value: c.value,
            comparison: c.operator || '=',
            operator: cIdx === 0 ? null : (group.conditionOperator ?? 'AND'),
          })),
      }));
    }
    if (Array.isArray(parsed)) {
      return parsed
        .filter((f: { field: string; value: string }) => f.field && f.value)
        .map((f: { field: string; operator: string; value: string }) => ({
          type: 'filter' as const,
          field: f.field,
          value: f.value,
          comparison: f.operator || '=',
          operator: null,
        }));
    }
    return [];
  } catch {
    return [];
  }
}
